import type { NavGroup, NavGroupId, NavItem } from './types';
import { phaseReaches, type OnboardingPhase } from '@/features/onboarding/phase';
import { getStartedNav } from './get-started.nav';
import { dashboardNav } from './dashboard.nav';
import { dtesNav } from './dtes.nav';
import { boletasNav } from './boletas.nav';
import { foliosNav } from './folios.nav';
import { enviosNav } from './envios.nav';
import { certificationNav } from './certification.nav';
import { companiesNav } from './companies.nav';
import { inboundNav } from './inbound.nav';
import { integrationsNav } from './integrations.nav';
import { webhooksNav } from './webhooks.nav';

/**
 * THIS FILE ONLY COMPOSES. Nobody adds a destination here: each feature owns
 * its `<feature>.nav.ts` and this module concatenates them in the navigation
 * tree's order (screen inventory §4.12).
 *
 * The reason is concurrency, not aesthetics: with twelve agents building
 * screens in parallel, a central array everybody edits produces SEMANTIC merge
 * conflicts — two adjacent entries added on different branches merge "clean"
 * and duplicate or lose a destination — that git cannot detect.
 *
 * «Cuenta» (views 4 and 5) does NOT appear: it is reached from the account menu
 * in the top bar, not from the sidebar (§4.12).
 */
const ALL_DESTINATIONS: readonly NavItem[] = [
  ...getStartedNav,
  ...dashboardNav,
  ...dtesNav,
  ...boletasNav,
  ...foliosNav,
  ...enviosNav,
  ...certificationNav,
  ...companiesNav,
  ...inboundNav,
  ...integrationsNav,
  ...webhooksNav,
];

/**
 * Removes from the tree whatever is marked `blockedByBackend`: the route can be
 * built and tested, but offering the link leads to a screen with nowhere to
 * get data from.
 *
 * **It is an exported function and not an inline `.filter` for a concrete
 * reason.** Today the production tree has no blocked destination (A-02 to A-05
 * of the roadmap closed the four there were), and a test asserting over
 * `ALL_DESTINATIONS` to verify the filter would pass green walking an empty set
 * — that is, it would stop testing anything exactly when there is nothing left
 * to test. With the rule here, its test exercises it against a test tree and
 * will still be true the day the next backend gap appears.
 */
export function withoutBackendBlocked(
  destinations: readonly NavItem[],
): readonly NavItem[] {
  return destinations
    .filter((d) => !d.blockedByBackend)
    .map((d) => {
      if (!d.children) return d;
      // A blocked child is hidden without touching its siblings: Certificación
      // has five destinations and at one point had two depending on the
      // backend; hiding the parent would have hidden the three that did work.
      const visible = d.children.filter((c) => !c.blockedByBackend);
      // All children blocked leaves a parent that expands to nothing. An item
      // with `children` does NOT navigate, so the destination is left inert: the
      // second level is dropped and the parent goes back to being a link.
      return visible.length === d.children.length
        ? d
        : { ...d, children: visible.length > 0 ? visible : undefined };
    });
}

/** The destinations handed to `AppShell`. */
export const NAV: readonly NavItem[] = withoutBackendBlocked(ALL_DESTINATIONS);

/** Unfiltered, for the tests that verify the whole tree. */
export const NAV_UNFILTERED = ALL_DESTINATIONS;

/**
 * The two blocks, in order. The label goes here and not in the
 * `<feature>.nav.ts` files because it is a composition decision: a feature
 * cannot know what the section it shares with nine others is called.
 */
const GROUPS: readonly { id: NavGroupId; label: string }[] = [
  { id: 'setup', label: 'Puesta en marcha' },
  { id: 'operation', label: 'Operación' },
];

/**
 * The lock reasons, by the phase still MISSING. One single table so the wording
 * does not spread across ten feature files: if tomorrow it has to be said
 * differently, it changes here.
 */
const REASON_BY_MISSING_PHASE: Readonly<Record<OnboardingPhase, string>> = {
  'no-company': 'Disponible siempre',
  'in-certification': 'Primero registra tu empresa',
  operating: 'Disponible cuando el SII autorice tu empresa',
};

/** The same for every destination the role does not reach: there are no
 *  nuances to make and the action is always the same. */
const REASON_WITHOUT_PERMISSION = 'Tu rol no incluye esta sección';

/**
 * The menu handed to `AppShell`, split into the two blocks and with the lock
 * already resolved for the phase the tenant is in and the role of whoever is
 * looking.
 *
 * It does three things, and the third is the one that matters:
 *
 * 1. It filters `blockedByBackend`, just like `NAV` (same reason: the link
 *    would lead to a screen with no data, and there is nothing the user can do
 *    about it).
 * 2. It distributes by `group`, in `GROUPS`' order, preserving the tree's order
 *    within each block.
 * 3. To the destination whose `minimumPhase` the current phase does not reach
 *    yet it attaches a `blockReason` — **it does not remove it**. That is the
 *    difference between the two recommendations this file implements: hiding
 *    what does not apply looked like the obvious answer to "I did not know what
 *    I had to do", and it is worse. An absent item teaches neither that it
 *    exists nor what unlocks it; and hiding by phase would have hidden
 *    «Integraciones» while the tenant certifies, which is the exact opposite of
 *    what the pricing promises.
 *
 * Both blocks start open. «Operación» used to start collapsed until the setup
 * was finished, which put DTEs one extra click away on every visit while
 * certifying; now each block stays the way the person left it (`AppShell`
 * remembers it).
 */
export function navByGroup(
  phase: OnboardingPhase,
  can: (permission: string) => boolean = () => true,
): readonly NavGroup[] {
  return GROUPS.map(({ id, label }) => ({
    id,
    label,
    // A destination with no `group` falls into «Operación» so the menu does not
    // lose it silently; `router.test.tsx` demands that none arrive that way.
    items: NAV.filter((d) => (d.group ?? 'operation') === id).map((d) =>
      withLock(d, phase, can),
    ),
  })).filter((group) => group.items.length > 0);
}

/**
 * It attaches the lock's reason to the destination — and to its children — when
 * the phase does not reach, or the role lacks the permission that destination
 * asks for.
 *
 * The permission goes FIRST: it is the reason that does not change on its own.
 * Somebody can wait to finish the setup, but a reader will not be able to
 * configure webhooks however long they wait, and telling them the section is
 * available once the SII authorises their company would send them waiting for
 * something that will not unblock them.
 *
 * `can` arrives as a parameter and not from a hook: this module is a
 * module-level constant (the same reason `AppLayout` attaches the badges).
 */
function withLock(
  destination: NavItem,
  phase: OnboardingPhase,
  can: (permission: string) => boolean,
): NavItem {
  const { minimumPhase, minimumPermission } = destination;

  // The `children` go with the destination in both cases: a locked parent does
  // not expand — there is nowhere to go — so the second level is unreachable by
  // construction, with no need to mark each child.
  if (minimumPermission !== undefined && !can(minimumPermission)) {
    return { ...destination, blockReason: REASON_WITHOUT_PERMISSION };
  }
  if (minimumPhase === undefined || phaseReaches(phase, minimumPhase)) return destination;
  return { ...destination, blockReason: REASON_BY_MISSING_PHASE[minimumPhase] };
}
